import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Database, AlertTriangle, HardDrive, ChevronDown, ChevronUp } from 'lucide-react';
import { authHeaders } from '../../services/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatBytes(mb) {
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  return `${(mb * 1024).toFixed(0)} KB`;
}

function StorageBar({ usedMb, label, maxMb }) {
  const pct = maxMb > 0 ? Math.min((usedMb / maxMb) * 100, 100) : 0;
  return (
    <div className="space-y-1">
      {label && <p className="text-[10px] text-slate-400">{label}</p>}
      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${pct > 80 ? 'bg-red-500' : pct > 50 ? 'bg-amber-400' : 'bg-gk-teal'}`}
          style={{ width: `${Math.max(pct, 1)}%` }}
        />
      </div>
    </div>
  );
}

export function StorageStatsPanel() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/super-admin/storage-stats`, { headers: authHeaders() });
        if (res.ok) setStats(await res.json());
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.error('Storage stats error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) {
    return (
      <Card className="bg-gk-navy-light/40 border-gk-teal/20 backdrop-blur-sm mb-6">
        <CardContent className="p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gk-teal"></div>
          <span className="ml-2 text-slate-400 text-sm">Loading storage stats...</span>
        </CardContent>
      </Card>
    );
  }

  if (!stats) return null;

  const alertBorder = stats.alert_level === 'critical' ? 'border-red-500/60' : stats.alert_level === 'warning' ? 'border-amber-500/60' : 'border-gk-teal/20';
  const alertBg = stats.alert_level === 'critical' ? 'bg-red-950/30' : stats.alert_level === 'warning' ? 'bg-amber-950/30' : 'bg-gk-navy-light/40';
  const maxGb = 10;
  const totalPct = (stats.total_storage_gb / maxGb) * 100;
  const totalMbForBars = stats.total_storage_mb;

  return (
    <Card data-testid="storage-stats-panel" className={`${alertBg} ${alertBorder} backdrop-blur-sm mb-6 border`}>
      <CardHeader className="pb-2 pt-4 px-5 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold text-white flex items-center gap-2">
          <Database className="w-4 h-4 text-gk-teal" />
          Database Storage
        </CardTitle>
        <button
          data-testid="toggle-storage-details"
          onClick={() => setExpanded(!expanded)}
          className="text-slate-400 hover:text-white transition-colors"
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </CardHeader>
      <CardContent className="px-5 pb-4">
        {/* Alert banner */}
        {stats.alert_level !== 'normal' && (
          <div data-testid="storage-alert" className={`flex items-center gap-2 p-2.5 rounded-lg mb-3 ${stats.alert_level === 'critical' ? 'bg-red-900/40 border border-red-500/50' : 'bg-amber-900/40 border border-amber-500/50'}`}>
            <AlertTriangle className={`w-4 h-4 flex-shrink-0 ${stats.alert_level === 'critical' ? 'text-red-400' : 'text-amber-400'}`} />
            <p className={`text-xs font-medium ${stats.alert_level === 'critical' ? 'text-red-300' : 'text-amber-300'}`}>
              {stats.alert_level === 'critical'
                ? `Storage usage is critical at ${stats.total_storage_gb} GB. Consider upgrading or archiving data.`
                : `Storage usage approaching limit at ${stats.total_storage_gb} GB. Monitor closely.`
              }
            </p>
          </div>
        )}

        {/* Overall stats */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-4">
            <div>
              <p className="text-2xl font-bold text-white">{formatBytes(stats.total_storage_mb)}</p>
              <p className="text-[10px] text-slate-500">Total Storage</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-slate-300">{formatBytes(stats.total_data_mb)}</p>
              <p className="text-[10px] text-slate-500">Data Size</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-slate-300">{(stats.total_documents || 0).toLocaleString()}</p>
              <p className="text-[10px] text-slate-500">Documents</p>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-slate-300">{stats.collection_count}</p>
              <p className="text-[10px] text-slate-500">Collections</p>
            </div>
          </div>
          <HardDrive className={`w-8 h-8 ${stats.alert_level === 'critical' ? 'text-red-400' : stats.alert_level === 'warning' ? 'text-amber-400' : 'text-gk-teal'}`} />
        </div>

        {/* Overall progress bar */}
        <div className="space-y-1">
          <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-700 ${totalPct > 80 ? 'bg-red-500' : totalPct > 50 ? 'bg-amber-400' : 'bg-gk-teal'}`}
              style={{ width: `${Math.max(totalPct, 0.5)}%` }}
            />
          </div>
          <div className="flex justify-between">
            <span className="text-[10px] text-slate-500">{stats.total_storage_gb} GB used</span>
            <span className="text-[10px] text-slate-500">
              <span className="text-amber-400">5 GB</span> / <span className="text-red-400">8 GB</span> / 10 GB
            </span>
          </div>
        </div>

        {/* Per-community breakdown */}
        {expanded && (
          <div data-testid="storage-community-breakdown" className="mt-4 pt-3 border-t border-slate-700/50 space-y-3">
            <p className="text-xs font-semibold text-slate-300">Per-Community Breakdown</p>
            {stats.communities.map((c) => (
              <div key={c.community_id} className="p-2.5 bg-slate-800/40 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs font-medium text-white truncate flex-1">{c.community_name}</p>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs text-slate-400">{c.document_count.toLocaleString()} docs</span>
                    <span className="text-xs font-semibold text-gk-teal">{formatBytes(c.storage_mb)}</span>
                  </div>
                </div>
                <StorageBar usedMb={c.storage_mb} maxMb={totalMbForBars} />
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
